import { useEffect, useState } from "react";
import { BufferAttribute, BufferGeometry, Color, Matrix3, Matrix4, Vector3 } from "three";
import type { AnimationClip, Material, Mesh, Object3D, Texture, WebGLRenderer } from "three";
import type { ModelDef } from "./catalog.js";
import { modelUrl } from "./catalog.js";

/**
 * Loading and baking of the built-in GLB models. A model is fetched once, decoded with the
 * self-hosted decoders (meshopt from the bundle, Draco and Basis under `/draco/` and `/basis/`),
 * and baked into ONE geometry: float positions and normals, material colors as vertex colors,
 * and per-vertex `aTint` (takes the instance tint) and `aGlow` (lights up at night). One geometry
 * means one draw call per model and level of detail, whatever the model's material count.
 */

export interface BakedModel {
  geometry: BufferGeometry;
  /** Size after normalisation (meters). */
  height: number;
  /** Radius of a bounding sphere centred at (0, height / 2, 0). */
  radius: number;
  triangles: number;
}

type GltfLike = { scene: Object3D; animations: AnimationClip[] };

let loaderPromise: Promise<(url: string) => Promise<GltfLike>> | null = null;

/** The shared glTF loader (lazy imports keep three's loaders out of the main chunk). */
export function gltfLoader(
  renderer: WebGLRenderer | null = null,
): Promise<(url: string) => Promise<GltfLike>> {
  loaderPromise ??= (async () => {
    const [{ GLTFLoader }, { DRACOLoader }, { KTX2Loader }, { MeshoptDecoder }] = await Promise.all(
      [
        import("three/examples/jsm/loaders/GLTFLoader.js"),
        import("three/examples/jsm/loaders/DRACOLoader.js"),
        import("three/examples/jsm/loaders/KTX2Loader.js"),
        import("three/examples/jsm/libs/meshopt_decoder.module.js"),
      ],
    );
    const loader = new GLTFLoader();
    loader.setDRACOLoader(new DRACOLoader().setDecoderPath("/draco/"));
    loader.setMeshoptDecoder(MeshoptDecoder);
    if (renderer)
      loader.setKTX2Loader(new KTX2Loader().setTranscoderPath("/basis/").detectSupport(renderer));
    return (url: string) => loader.loadAsync(url);
  })();
  return loaderPromise;
}

const gltfs = new Map<string, Promise<GltfLike>>();

/** One fetch and decode per URL, shared by every consumer. */
export function loadGltf(file: string): Promise<GltfLike> {
  let p = gltfs.get(file);
  if (!p) {
    p = gltfLoader().then((load) => load(modelUrl(file)));
    p.catch(() => gltfs.delete(file));
    gltfs.set(file, p);
  }
  return p;
}

const colorOf = (mat: Material): Color => {
  const c = (mat as Material & { color?: Color }).color;
  return c ? c.clone() : new Color(1, 1, 1);
};

/** Bakes a scene into one geometry (see the module comment). Pure: no network, unit tested. */
export function bakeScene(
  scene: Object3D,
  def: Pick<ModelDef, "height" | "length" | "quarterTurns" | "tint" | "glow" | "palette">,
): BakedModel {
  scene.updateMatrixWorld(true);
  const pos: number[] = [];
  const nor: number[] = [];
  const col: number[] = [];
  const tint: number[] = [];
  const glow: number[] = [];
  const index: number[] = [];
  const v = new Vector3();
  const n3 = new Matrix3();
  const tintSet = new Set(def.tint ?? []);
  scene.traverse((o) => {
    const mesh = o as Mesh;
    if (!mesh.isMesh) return;
    const g = mesh.geometry;
    const p = g.getAttribute("position");
    const nm = g.getAttribute("normal");
    const mat = Array.isArray(mesh.material) ? mesh.material[0]! : mesh.material;
    const name = mat.name;
    const color = def.palette?.[name] ? new Color(def.palette[name]) : colorOf(mat);
    const t = tintSet.has(name) ? 1 : 0;
    const gl = def.glow?.[name] ?? 0;
    const base = pos.length / 3;
    n3.getNormalMatrix(mesh.matrixWorld);
    for (let i = 0; i < p.count; i++) {
      v.fromBufferAttribute(p, i).applyMatrix4(mesh.matrixWorld);
      pos.push(v.x, v.y, v.z);
      if (nm) v.fromBufferAttribute(nm, i).applyMatrix3(n3).normalize();
      else v.set(0, 1, 0);
      nor.push(v.x, v.y, v.z);
      col.push(color.r, color.g, color.b);
      tint.push(t);
      glow.push(gl);
    }
    if (g.index) for (let i = 0; i < g.index.count; i++) index.push(base + g.index.getX(i));
    else for (let i = 0; i < p.count; i++) index.push(base + i);
  });
  return finishBake(pos, nor, col, tint, glow, index, def);
}

function finishBake(
  pos: number[],
  nor: number[],
  col: number[],
  tint: number[],
  glow: number[],
  index: number[],
  def: Pick<ModelDef, "height" | "length" | "quarterTurns">,
): BakedModel {
  const count = pos.length / 3;
  const min = new Vector3(Infinity, Infinity, Infinity);
  const max = new Vector3(-Infinity, -Infinity, -Infinity);
  for (let i = 0; i < count; i++) {
    min.min(v3(pos, i));
    max.max(v3(pos, i));
  }
  const rot = new Matrix4();
  let turns = def.quarterTurns ?? 0;
  // A vehicle's long horizontal axis is Z; headlights (glow 1) decide which end is the front.
  if (def.length) {
    if (max.x - min.x > max.z - min.z) turns += 1;
  }
  rot.makeRotationY((turns * Math.PI) / 2);
  const front = def.length ? frontSign(pos, glow, rot) : 1;
  if (front < 0) rot.premultiply(new Matrix4().makeRotationY(Math.PI));
  const n3 = new Matrix3().setFromMatrix4(rot);
  const v = new Vector3();
  min.set(Infinity, Infinity, Infinity);
  max.set(-Infinity, -Infinity, -Infinity);
  for (let i = 0; i < count; i++) {
    v.set(pos[i * 3]!, pos[i * 3 + 1]!, pos[i * 3 + 2]).applyMatrix4(rot);
    pos[i * 3] = v.x;
    pos[i * 3 + 1] = v.y;
    pos[i * 3 + 2] = v.z;
    min.min(v);
    max.max(v);
    v.set(nor[i * 3]!, nor[i * 3 + 1]!, nor[i * 3 + 2])
      .applyMatrix3(n3)
      .normalize();
    nor[i * 3] = v.x;
    nor[i * 3 + 1] = v.y;
    nor[i * 3 + 2] = v.z;
  }
  const size = def.length ? max.z - min.z : def.height ? max.y - min.y : 1;
  const target = def.length ?? def.height ?? size;
  const s = size > 1e-6 ? target / size : 1;
  const cx = (min.x + max.x) / 2;
  const cz = (min.z + max.z) / 2;
  let radius = 0;
  for (let i = 0; i < count; i++) {
    pos[i * 3] = (pos[i * 3]! - cx) * s;
    pos[i * 3 + 1] = (pos[i * 3 + 1]! - min.y) * s;
    pos[i * 3 + 2] = (pos[i * 3 + 2]! - cz) * s;
  }
  const height = (max.y - min.y) * s;
  for (let i = 0; i < count; i++) {
    radius = Math.max(
      radius,
      Math.hypot(pos[i * 3]!, pos[i * 3 + 1]! - height / 2, pos[i * 3 + 2]!),
    );
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new BufferAttribute(new Float32Array(pos), 3));
  geometry.setAttribute("normal", new BufferAttribute(new Float32Array(nor), 3));
  geometry.setAttribute("color", new BufferAttribute(new Float32Array(col), 3));
  geometry.setAttribute("aTint", new BufferAttribute(new Float32Array(tint), 1));
  geometry.setAttribute("aGlow", new BufferAttribute(new Float32Array(glow), 1));
  geometry.setIndex(
    new BufferAttribute(count > 65535 ? new Uint32Array(index) : new Uint16Array(index), 1),
  );
  geometry.computeBoundingSphere();
  return { geometry, height, radius, triangles: index.length / 3 };
}

const v3 = (a: number[], i: number) => new Vector3(a[i * 3], a[i * 3 + 1], a[i * 3 + 2]);

/** +1 when the headlights end up at +Z after `rot`, else -1 (1 when there are none). */
function frontSign(pos: number[], glow: number[], rot: Matrix4): number {
  let sum = 0;
  let n = 0;
  let zs = 0;
  const v = new Vector3();
  for (let i = 0; i < glow.length; i++) {
    v.set(pos[i * 3]!, pos[i * 3 + 1]!, pos[i * 3 + 2]).applyMatrix4(rot);
    zs += v.z;
    if (glow[i] === 1) {
      sum += v.z;
      n++;
    }
  }
  if (n === 0) return 1;
  return sum / n >= zs / glow.length ? 1 : -1;
}

const baked = new Map<string, Promise<BakedModel>>();
const settled = new Map<string, BakedModel>();

const keyOf = (file: string, def: ModelDef) => `${def.file}|${file}`;

/** The baked model for a file of `def` (its main file or its lower level of detail), loaded once. */
export function loadBaked(file: string, def: ModelDef): Promise<BakedModel> {
  const key = keyOf(file, def);
  let p = baked.get(key);
  if (!p) {
    p = loadGltf(file).then((g) => {
      const m = bakeScene(g.scene, def);
      settled.set(key, m);
      return m;
    });
    p.catch(() => baked.delete(key));
    baked.set(key, p);
  }
  return p;
}

/** The levels of detail of a model (index 0 is the full one), null while loading; re-renders on arrival. */
export function useBaked(def: ModelDef | null): (BakedModel | null)[] {
  const [, force] = useState(0);
  const files = def ? [def.file, ...(def.lod1 ? [def.lod1] : [])] : [];
  const key = files.join("|");
  useEffect(() => {
    if (!def) return;
    let alive = true;
    for (const f of files) {
      loadBaked(f, def).then(
        () => alive && force((n) => n + 1),
        () => undefined,
      );
    }
    return () => {
      alive = false;
    };
  }, [key]);
  return files.map((f) => (def ? (settled.get(keyOf(f, def)) ?? null) : null));
}

export type { Texture };
